import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CatalogView = 'list' | 'grid';

/** Granular notification + privacy preferences (stored locally; bot honours them when wired up). */
export interface NotifPrefs {
  enabled: boolean; // master switch
  orderStatus: boolean;
  paymentApproved: boolean;
  paymentRejected: boolean;
  serviceActivated: boolean;
  discounts: boolean;
  newProducts: boolean;
  specialOffers: boolean;
  sound: boolean;
  vibration: boolean;
  quietHours: boolean;
}

export type NotifKey = keyof NotifPrefs;
export type AppPrefKey = 'haptics' | 'reduceMotion';
export type UiPrefKey = NotifKey | AppPrefKey;

interface UiState {
  catalogView: CatalogView;
  setCatalogView: (v: CatalogView) => void;

  notif: NotifPrefs;

  // App-level preferences
  haptics: boolean;
  reduceMotion: boolean;

  setPref: (key: UiPrefKey, value: boolean) => void;
}

const DEFAULT_NOTIF: NotifPrefs = {
  enabled: true,
  orderStatus: true,
  paymentApproved: true,
  paymentRejected: true,
  serviceActivated: true,
  discounts: false,
  newProducts: false,
  specialOffers: false,
  sound: true,
  vibration: true,
  quietHours: false,
};

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      catalogView: 'list',
      setCatalogView: (catalogView) => set({ catalogView }),

      notif: DEFAULT_NOTIF,
      haptics: true,
      reduceMotion: false,

      setPref: (key, value) =>
        set((s) => {
          if (key === 'haptics' || key === 'reduceMotion') {
            return { [key]: value } as Partial<UiState>;
          }
          return { notif: { ...s.notif, [key]: value } };
        }),
    }),
    {
      name: 'premium-bot-ui',
      partialize: (s) => ({
        catalogView: s.catalogView,
        notif: s.notif,
        haptics: s.haptics,
        reduceMotion: s.reduceMotion,
      }),
    },
  ),
);
